import { t } from "@/lib/i18n/interpolate";
import type {
  DocBlock,
  DocRun,
  DocSection,
  DocumentModel,
} from "@/lib/tool-kit/document";

/**
 * A printable peer-review worksheet: name lines, short instructions, a rating grid with
 * one row per criterion, open questions with ruled space to answer, and two whole-draft
 * questions. Unlike a grading rubric it has no performance descriptors and no points —
 * it asks a classmate to notice and explain, which is what makes it formative.
 */

export type PeerQuestionStyle = "both" | "rating" | "open";

export interface PeerCriterion {
  id: string;
  name: string;
  question: string;
}

export interface PeerReviewInput {
  title: string;
  criteria: PeerCriterion[];
  style: PeerQuestionStyle;
  /** "4" or "5" points. */
  scale: string;
  /** Ruled lines under each open question: "2"–"5". */
  lines: string;
  header: boolean;
  overall: boolean;
}

export interface PeerReviewLabels {
  defaultTitle: string;
  fields: { writer: string; reviewer: string; draft: string; date: string };
  instructions: string;
  headings: { rating: string; open: string; overall: string };
  criterionColumn: string;
  scaleNote: string;
  scale4: readonly string[];
  scale5: readonly string[];
  overallQuestions: readonly string[];
}

const BOX = "☐";
const MIN_LINES = 1;
const MAX_LINES = 8;

export function lineCount(raw: string): number {
  const value = Number.parseInt(raw, 10);
  if (!Number.isFinite(value)) {
    return 3;
  }
  return Math.min(MAX_LINES, Math.max(MIN_LINES, value));
}

/** The criteria that have something to ask — a row with no name and no question is skipped. */
export function usableCriteria(
  criteria: readonly PeerCriterion[]
): PeerCriterion[] {
  return criteria.filter(
    (criterion) =>
      criterion.name.trim() !== "" || criterion.question.trim() !== ""
  );
}

function criterionText(criterion: PeerCriterion): DocRun[] {
  const name = criterion.name.trim();
  const question = criterion.question.trim();
  if (name === "") {
    return [{ text: question }];
  }
  if (question === "") {
    return [{ text: name, bold: true }];
  }
  return [{ text: `${name}:`, bold: true }, { text: ` ${question}` }];
}

function scaleLabels(input: PeerReviewInput, labels: PeerReviewLabels) {
  return input.scale === "5" ? labels.scale5 : labels.scale4;
}

function ratingSection(
  input: PeerReviewInput,
  labels: PeerReviewLabels,
  criteria: PeerCriterion[]
): DocSection {
  const levels = scaleLabels(input, labels);
  const legend = levels
    .map((level, index) => `${index + 1} = ${level}`)
    .join(" · ");
  return {
    heading: labels.headings.rating,
    blocks: [
      { kind: "guidance", text: t(labels.scaleNote, { scale: legend }) },
      {
        kind: "table",
        head: [
          labels.criterionColumn,
          ...levels.map((_, index) => String(index + 1)),
        ],
        rows: criteria.map((criterion) => [
          criterionText(criterion),
          ...levels.map(() => BOX),
        ]),
      },
    ],
  };
}

function openSection(
  input: PeerReviewInput,
  labels: PeerReviewLabels,
  criteria: PeerCriterion[]
): DocSection {
  const count = lineCount(input.lines);
  return {
    heading: labels.headings.open,
    blocks: criteria.flatMap((criterion): DocBlock[] => [
      { kind: "paragraph", text: criterionText(criterion) },
      { kind: "lines", count },
    ]),
  };
}

export function buildPeerReview(
  input: PeerReviewInput,
  labels: PeerReviewLabels
): DocumentModel {
  const criteria = usableCriteria(input.criteria);
  const lines = lineCount(input.lines);
  const sections: DocSection[] = [];
  const intro: DocBlock[] = [];
  if (input.header) {
    intro.push({
      kind: "fields",
      fields: [
        { label: labels.fields.writer },
        { label: labels.fields.reviewer },
        { label: labels.fields.draft },
        { label: labels.fields.date },
      ],
    });
  }
  intro.push({ kind: "guidance", text: labels.instructions });
  sections.push({ blocks: intro });
  if (criteria.length > 0 && input.style !== "open") {
    sections.push(ratingSection(input, labels, criteria));
  }
  if (criteria.length > 0 && input.style !== "rating") {
    sections.push(openSection(input, labels, criteria));
  }
  if (input.overall) {
    sections.push({
      heading: labels.headings.overall,
      blocks: labels.overallQuestions.flatMap((question): DocBlock[] => [
        { kind: "paragraph", text: question },
        { kind: "lines", count: lines },
      ]),
    });
  }
  return {
    title: input.title.trim() || labels.defaultTitle,
    sections,
  };
}
